import React, { useState } from 'react';
import { PlusCircle, Edit, Trash2 } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import SortableTable from '../../../components/ui/SortableTable';
import ShareholderForm from '../forms/ShareholderForm';
import ApiService from '../../../services/apiService';

const ShareholdersPage = ({ shareholders, onDataRefresh, addMessage, selectedCompany, userRole }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [editingShareholder, setEditingShareholder] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const handleCreateShareholder = async (data) => {
        if (!selectedCompany) {
            addMessage("No company selected.", 'error');
            return;
        }
        try {
            await ApiService.createShareholder({ ...data, company_id: selectedCompany.id });
            onDataRefresh();
            setShowCreateModal(false);
            addMessage('Shareholder created successfully!', 'success');
        } catch (error) {
            addMessage(`Error creating shareholder: ${error.message}`, 'error');
        }
    };

    const handleUpdateShareholder = async (id, data) => {
        try {
            await ApiService.updateShareholder(id, data);
            onDataRefresh();
            setShowEditModal(false);
            setEditingShareholder(null);
            addMessage('Shareholder updated successfully!', 'success');
        } catch (error) {
            addMessage(`Error updating shareholder: ${error.message}`, 'error');
        }
    };

    const handleDeleteClick = (shareholder) => {
        setItemToDelete(shareholder);
        setShowConfirmDelete(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await ApiService.deleteShareholder(itemToDelete.id);
            onDataRefresh();
            setShowConfirmDelete(false);
            setItemToDelete(null);
            addMessage('Shareholder deleted successfully!', 'success');
        } catch (error) {
            addMessage(`Error deleting shareholder: ${error.message}`, 'error');
        }
    };

    const handleEditClick = (shareholder) => {
        setEditingShareholder(shareholder);
        setShowEditModal(true);
    };

    const getShareholderName = (shareholder) => {
        // Debug: log shareholder data
        console.log('Shareholder data:', shareholder);
        
        // Check entity_type (case-insensitive)
        const entityType = (shareholder.entity_type || '').toLowerCase();
        
        if (entityType === 'individual') {
            // For individuals, use first_name + last_name, fallback to name field
            const fullName = `${shareholder.first_name || ''} ${shareholder.last_name || ''}`.trim();
            const result = fullName || shareholder.name || 'N/A';
            console.log('Individual name result:', result);
            return result;
        } else {
            // For entities (companies, trusts, etc.), use name or company_name
            const result = shareholder.name || shareholder.company_name || 'N/A';
            console.log('Entity name result:', result);
            return result;
        }
    };

    const shareholdersTableColumns = [
        { 
            key: 'name', 
            header: 'Name', 
            isSortable: true,
            render: (row) => getShareholderName(row)
        },
        { key: 'entity_type', header: 'Entity Type', isSortable: true },
        { key: 'type', header: 'Type', isSortable: true },
        { key: 'email', header: 'Email', isSortable: true },
        { 
            key: 'actions', 
            header: 'Actions', 
            isSortable: false,
            render: (row) => (
                userRole !== 'viewer' && (
                    <div className="flex space-x-2">
                        <button
                            onClick={() => handleEditClick(row)}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => handleDeleteClick(row)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                )
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Shareholders</h2>
                {userRole !== 'viewer' && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <PlusCircle className="h-5 w-5 mr-2" />
                        Add Shareholder
                    </button>
                )}
            </div>

            {!selectedCompany ? (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    Please select a company to view shareholders.
                </div>
            ) : (
                <SortableTable
                    data={shareholders}
                    columns={shareholdersTableColumns}
                    entityType="shareholder"
                    addMessage={addMessage}
                />
            )}

            {showCreateModal && (
                <Modal onClose={() => setShowCreateModal(false)} title="Add Shareholder">
                    <ShareholderForm
                        onSubmit={handleCreateShareholder}
                        onCancel={() => setShowCreateModal(false)}
                    />
                </Modal>
            )}

            {showEditModal && editingShareholder && (
                <Modal onClose={() => setShowEditModal(false)} title="Edit Shareholder">
                    <ShareholderForm
                        initialData={editingShareholder}
                        onSubmit={(data) => handleUpdateShareholder(editingShareholder.id, data)}
                        onCancel={() => {
                            setShowEditModal(false);
                            setEditingShareholder(null);
                        }}
                    />
                </Modal>
            )}

            {showConfirmDelete && (
                <Modal onClose={() => setShowConfirmDelete(false)} title="Confirm Delete">
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Are you sure you want to delete this shareholder? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                onClick={() => setShowConfirmDelete(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ShareholdersPage;